import type { FieldOption } from "@/fields";

/*
 * The list item fields as people read them (phase-2-list-item-fields.md D-3).
 * The generated client types these fields as plain strings (its codegen writes
 * `string` for every enum), so each closed set is declared here once, and a
 * value read from the API goes through one of the guards at the bottom before
 * it picks a label.
 */

/** The picker order: Not set first (no choice yet), then Now, Next, Later. The list shows them in the same order (`PRIORITY_LIST_ORDER`). */
export const LIST_ITEM_PRIORITY_VALUES = [
  "NOT_SET",
  "NOW",
  "NEXT",
  "LATER",
] as const;

/** The order the Type picker lists them in (the office list's type filter has its own). */
export const LIST_ITEM_WORK_TYPE_VALUES = [
  "PROJECT",
  "SERVICE",
  "FIX",
] as const;

const LIST_ITEM_PROPOSED_BY_VALUES = ["MEMBER", "BIRDWATCH", "BOTH"] as const;

/** When a to-do should be done (`ListItem.timeframe`), nearest first. */
export const LIST_ITEM_TIMEFRAME_VALUES = [
  "THIS_MONTH",
  "NEXT_MONTH",
  "NEXT_3_MONTHS",
  "NEXT_6_MONTHS",
  "NEXT_12_MONTHS",
  "LATER",
] as const;

/** The statuses, in the order the office's status menu lists them. ARCHIVED is an office-only state. */
export const LIST_ITEM_STATUS_VALUES = [
  "TODO",
  "IN_PROGRESS",
  "DONE",
  "ARCHIVED",
] as const;

export type ListItemPriorityValue = (typeof LIST_ITEM_PRIORITY_VALUES)[number];
export type ListItemWorkTypeValue = (typeof LIST_ITEM_WORK_TYPE_VALUES)[number];
export type ListItemProposedByValue =
  (typeof LIST_ITEM_PROPOSED_BY_VALUES)[number];
export type ListItemStatusValue = (typeof LIST_ITEM_STATUS_VALUES)[number];
export type ListItemTimeframeValue =
  (typeof LIST_ITEM_TIMEFRAME_VALUES)[number];

/** The statuses still to be done ("open"); DONE and ARCHIVED are "closed". */
export const LIST_ITEM_OPEN_STATUSES = [
  "TODO",
  "IN_PROGRESS",
] as const satisfies readonly ListItemStatusValue[];

export const PRIORITY_LABELS: Record<ListItemPriorityValue, string> = {
  NOT_SET: "Not set",
  NOW: "Now",
  NEXT: "Next",
  LATER: "Later",
};

export const TIMEFRAME_LABELS: Record<ListItemTimeframeValue, string> = {
  THIS_MONTH: "This month",
  NEXT_MONTH: "Next month",
  NEXT_3_MONTHS: "Next 3 months",
  NEXT_6_MONTHS: "Next 6 months",
  NEXT_12_MONTHS: "Next 12 months",
  LATER: "Later",
};

export const WORK_TYPE_LABELS: Record<ListItemWorkTypeValue, string> = {
  PROJECT: "Project",
  SERVICE: "Service",
  // A handyman service: a dedicated block of time with a Birdwatch technician
  // for the home's to-dos (its meaning, in WORK_TYPE_SPECS, says so). Just
  // "Fix", the team's feedback (2026-10-30). The order that works these to-dos
  // keeps its own name, a Birdwatch Fix visit.
  FIX: "Fix",
};

export const STATUS_LABELS: Record<ListItemStatusValue, string> = {
  TODO: "To do",
  IN_PROGRESS: "In progress",
  DONE: "Done",
  ARCHIVED: "Archived",
};

/** What the office reads (phase-2-list-item-fields.md D-3). */
export const PROPOSED_BY_OFFICE_LABELS: Record<
  ListItemProposedByValue,
  string
> = {
  MEMBER: "Member",
  BIRDWATCH: "Birdwatch recommends",
  BOTH: "Member + Birdwatch",
};

/** What a member reads: nothing on their own items. */
export const PROPOSED_BY_MEMBER_LABELS: Record<
  ListItemProposedByValue,
  string | null
> = {
  MEMBER: null,
  BIRDWATCH: "Birdwatch recommends",
  BOTH: "Birdwatch recommends",
};

export const PRIORITY_OPTIONS: FieldOption[] = LIST_ITEM_PRIORITY_VALUES.map(
  (value) => ({ value, label: PRIORITY_LABELS[value] }),
);

/** The Timeframe picker's choice that clears it: the API stores no timeframe for it. */
export const TIMEFRAME_NOT_SET = "NOT_SET";

export const TIMEFRAME_OPTIONS: FieldOption[] = [
  { value: TIMEFRAME_NOT_SET, label: "Not set" },
  ...LIST_ITEM_TIMEFRAME_VALUES.map((value) => ({
    value,
    label: TIMEFRAME_LABELS[value],
  })),
];

export const WORK_TYPE_OPTIONS: FieldOption[] = LIST_ITEM_WORK_TYPE_VALUES.map(
  (value) => ({ value, label: WORK_TYPE_LABELS[value] }),
);

export const PROPOSED_BY_OPTIONS: FieldOption[] =
  LIST_ITEM_PROPOSED_BY_VALUES.map((value) => ({
    value,
    label: PROPOSED_BY_OFFICE_LABELS[value],
  }));

/** Whether a priority read from the API is one of the four buckets. */
export function isListItemPriority(
  value: string | null | undefined,
): value is ListItemPriorityValue {
  return LIST_ITEM_PRIORITY_VALUES.some((known) => known === value);
}

/** Whether a timeframe read from the API is one of the six (false for null: none chosen). */
export function isListItemTimeframe(
  value: string | null | undefined,
): value is ListItemTimeframeValue {
  return LIST_ITEM_TIMEFRAME_VALUES.some((known) => known === value);
}

/** Whether a work type read from the API is Project, Service or Fix (false for null: not classified yet). */
export function isListItemWorkType(
  value: string | null | undefined,
): value is ListItemWorkTypeValue {
  return LIST_ITEM_WORK_TYPE_VALUES.some((known) => known === value);
}

/** Whether a "proposed by" value read from the API is one of the three. */
export function isListItemProposedBy(
  value: string | null | undefined,
): value is ListItemProposedByValue {
  return LIST_ITEM_PROPOSED_BY_VALUES.some((known) => known === value);
}

/** Whether a status read from the API is one of the four. */
export function isListItemStatus(
  value: string | null | undefined,
): value is ListItemStatusValue {
  return LIST_ITEM_STATUS_VALUES.some((known) => known === value);
}

/** Whether a status read from the API is still to be done: To do or In progress. */
export function isOpenListItemStatus(
  value: string | null | undefined,
): boolean {
  return LIST_ITEM_OPEN_STATUSES.some((open) => open === value);
}
